import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGetMutationFn from '../use-get-mutation-fn';
import { assetKeys } from './assets.query';

/** One reverse-image match returned by `POST /api/admin/assets/match-stolen`. */
export interface StolenMatch {
  asid: number;
  uuid: string;
  eid: number | null;
  uid: number | null;
  oid: number | null;
  toid: number | null;
  public_url: string | null;
  phash: string | null;
  hamming_distance: number;
  date_added: string | null;
}

export interface MatchStolenResult {
  query_phash: string;
  max_hamming: number;
  match_count: number;
  matches: StolenMatch[];
}

export interface MatchStolenPayload {
  url: string;
  max_hamming?: number;
  limit?: number;
}

/** The admin proxy wraps some responses as `{ status, data }`; unwrap either. */
const unwrapMatch = (raw: any): MatchStolenResult => {
  const body = raw?.data ?? raw ?? {};
  return {
    query_phash: body.query_phash ?? '',
    max_hamming: body.max_hamming ?? 0,
    match_count: body.match_count ?? (Array.isArray(body.matches) ? body.matches.length : 0),
    matches: Array.isArray(body.matches) ? body.matches : [],
  };
};

/** Soft-delete a single asset (moderation). Backend sets deleted=1, active=0. */
export const useDeleteAssetMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<{ asid: number }, unknown>({
    defaultErrorMessage: 'Failed to delete the asset',
    endpoint: ({ asid }) => `/api/admin/assets/${asid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
};

/** Restore a soft-deleted asset. Backend sets deleted=0, active=1. */
export const useRestoreAssetMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<{ asid: number }, unknown>({
    defaultErrorMessage: 'Failed to restore the asset',
    endpoint: ({ asid }) => `/api/admin/assets/${asid}/restore`,
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
};

/**
 * Delete or restore a page selection, one request per asset, and refetch once
 * at the end. A stopgap until bulk jobs exist server-side (ff-api#1526 phase 4):
 * it is bounded by the page size and reports per-asset failures.
 */
export const useBulkAssetMutation = () => {
  const queryClient = useQueryClient();
  const remove = useGetMutationFn<{ asid: number }, unknown>({
    defaultErrorMessage: 'Failed to delete the asset',
    endpoint: ({ asid }) => `/api/admin/assets/${asid}`,
    method: 'DELETE',
  });
  const restore = useGetMutationFn<{ asid: number }, unknown>({
    defaultErrorMessage: 'Failed to restore the asset',
    endpoint: ({ asid }) => `/api/admin/assets/${asid}/restore`,
    method: 'POST',
  });

  return useMutation({
    mutationFn: async ({ action, asids }: { action: 'delete' | 'restore'; asids: number[] }) => {
      const call = action === 'delete' ? remove : restore;
      const failed: number[] = [];
      for (const asid of asids) {
        try {
          await call({ asid });
        } catch {
          failed.push(asid);
        }
      }
      return { done: asids.length - failed.length, failed };
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
};

/** Reverse-image lookup by perceptual hash (find stolen/duplicate photos). */
export const useMatchStolenMutation = () => {
  const mutationFn = useGetMutationFn<MatchStolenPayload, unknown>({
    defaultErrorMessage: 'Reverse-image lookup failed',
    endpoint: '/api/admin/assets/match-stolen',
    method: 'POST',
  });

  return useMutation({
    mutationFn: async (payload: MatchStolenPayload) =>
      unwrapMatch(await mutationFn({ max_hamming: 8, limit: 25, ...payload })),
  });
};
